import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Settings } from 'lucide-react';
import { TemplateContent } from '../../../../src/schemas/templateContent';

interface MetaFieldsEditorProps {
  content: TemplateContent;
  onChange: (content: TemplateContent) => void;
}

export function MetaFieldsEditor({ content, onChange }: MetaFieldsEditorProps) {
  const [isAddingField, setIsAddingField] = useState(false);
  const [newField, setNewField] = useState({
    key: '',
    label: '',
    type: 'text' as const,
    readOnly: false
  });

  const updateMetaField = (index: number, updates: any) => {
    const newMetaFields = content.metaFields.map((field: any, i: number) => 
      i === index ? { ...field, ...updates } : field
    );
    onChange({ ...content, metaFields: newMetaFields });
  };

  const removeMetaField = (index: number) => {
    const newMetaFields = content.metaFields.filter((_: any, i: number) => i !== index);
    onChange({ ...content, metaFields: newMetaFields });
  };

  const addMetaField = () => {
    if (!newField.key || !newField.label) return;

    const newMetaFields = [...content.metaFields, { ...newField }];
    onChange({ ...content, metaFields: newMetaFields });
    
    setNewField({
      key: '',
      label: '',
      type: 'text',
      readOnly: false
    });
    setIsAddingField(false);
  };

  const getFieldTypeLabel = (type: string) => {
    const labels = {
      auto: 'Auto-filled',
      text: 'Text',
      textarea: 'Text Area',
      number: 'Number'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getFieldTypeBadgeColor = (type: string) => {
    const colors = {
      auto: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      text: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      textarea: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      number: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  };

  const isDefaultField = (key: string) => {
    return ['care_home', 'auditor_name', 'date_of_audit', 'job_role'].includes(key);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Template Metadata Fields</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Configure the header information collected for each audit
            </p>
          </div>
          <Button 
            onClick={() => setIsAddingField(true)}
            size="sm"
            data-testid="button-add-meta-field"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Field
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Global Template Settings */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Enable Scoring</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow scoring and notifications based on audit results
                </p>
              </div>
              <Switch
                checked={content.scoring_enabled ?? true}
                onCheckedChange={(scoring_enabled) => onChange({ ...content, scoring_enabled })}
                data-testid="switch-template-scoring-enabled"
              />
            </div>
          </div>
          
          <Separator />
          
          {/* Existing Meta Fields */}
          {content.metaFields.map((field, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div>
                    <div className="font-medium">{field.label}</div>
                    <div className="text-sm text-muted-foreground">Key: {field.key}</div>
                  </div>
                  <Badge className={getFieldTypeBadgeColor(field.type)}>
                    {getFieldTypeLabel(field.type)}
                  </Badge>
                  {field.readOnly && (
                    <Badge variant="outline">Read-only</Badge>
                  )}
                  {isDefaultField(field.key) && (
                    <Badge variant="secondary">Default</Badge>
                  )}
                </div>
                {!isDefaultField(field.key) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMetaField(index)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`button-remove-meta-field-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Field Label</Label>
                  <Input
                    value={field.label}
                    onChange={(e) => updateMetaField(index, { label: e.target.value })}
                    className="mt-1"
                    disabled={isDefaultField(field.key)}
                    data-testid={`input-meta-label-${index}`}
                  />
                </div>
                <div>
                  <Label>Field Key</Label>
                  <Input
                    value={field.key}
                    onChange={(e) => updateMetaField(index, { key: e.target.value })}
                    className="mt-1"
                    disabled={isDefaultField(field.key)}
                    data-testid={`input-meta-key-${index}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Field Type</Label>
                  <Select 
                    value={field.type} 
                    onValueChange={(value) => updateMetaField(index, { type: value })}
                    disabled={isDefaultField(field.key)}
                  >
                    <SelectTrigger className="mt-1" data-testid={`select-meta-type-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-filled</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="textarea">Text Area</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <Switch
                    checked={field.readOnly || false}
                    onCheckedChange={(readOnly) => updateMetaField(index, { readOnly })}
                    disabled={isDefaultField(field.key)}
                    data-testid={`switch-meta-readonly-${index}`}
                  />
                  <Label>Read-only</Label>
                </div>
              </div>
            </div>
          ))}

          {/* Add New Field Form */}
          {isAddingField && (
            <>
              <Separator />
              <div className="border-2 border-dashed rounded-lg p-4">
                <h4 className="font-medium mb-3">Add New Meta Field</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Field Label</Label>
                    <Input
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      placeholder="e.g., Inspector Name"
                      className="mt-1"
                      data-testid="input-new-meta-label"
                    />
                  </div>
                  <div>
                    <Label>Field Key</Label>
                    <Input
                      value={newField.key}
                      onChange={(e) => setNewField({ ...newField, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="e.g., inspector_name"
                      className="mt-1"
                      data-testid="input-new-meta-key"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>Field Type</Label>
                    <Select 
                      value={newField.type} 
                      onValueChange={(value: any) => setNewField({ ...newField, type: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="select-new-meta-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-filled</SelectItem>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="textarea">Text Area</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2 mt-6">
                    <Switch
                      checked={newField.readOnly}
                      onCheckedChange={(readOnly) => setNewField({ ...newField, readOnly })}
                      data-testid="switch-new-meta-readonly"
                    />
                    <Label>Read-only</Label>
                  </div>
                </div>

                <div className="flex space-x-2 mt-4">
                  <Button 
                    onClick={addMetaField}
                    disabled={!newField.key || !newField.label}
                    data-testid="button-confirm-add-meta"
                  >
                    Add Field
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsAddingField(false);
                      setNewField({
                        key: '',
                        label: '',
                        type: 'text',
                        readOnly: false
                      });
                    }}
                    data-testid="button-cancel-add-meta"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}

          {content.metaFields.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No metadata fields configured</p>
              <p className="text-sm">Add fields to collect information for each audit</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}